/**
 * Admin Edit User Modal Component
 *
 * Modal for editing user details and tenant membership roles.
 *
 * @fileoverview Admin user edit modal component
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  VStack,
  HStack,
  useColorModeValue,
  Text,
  Alert,
  AlertIcon,
  Divider,
  Box,
  Badge,
} from '@chakra-ui/react';
import type { AdminUser, UpdateAdminUserInput, GlobalRole, TenantRole, UpdateMembershipInput } from '../../types/admin';

interface AdminEditUserModalProps {
  /** Modal open state */
  isOpen: boolean;
  /** Close callback */
  onClose: () => void;
  /** User to edit */
  user: AdminUser | null;
  /** Save user callback */
  onSave: (data: UpdateAdminUserInput) => Promise<boolean>;
  /** Update membership callback */
  onUpdateMembership?: (userId: string, tenantId: string, data: UpdateMembershipInput) => Promise<AdminUser | null>;
  /** Loading state */
  loading: boolean;
}

/**
 * Admin Edit User Modal Component
 *
 * Allows editing user display name, global role, and tenant membership roles.
 *
 * @param props - Component props
 * @returns React component
 *
 * @example
 * <AdminEditUserModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   user={selectedUser}
 *   onSave={handleSave}
 *   onUpdateMembership={handleUpdateMembership}
 *   loading={loading}
 * />
 */
export const AdminEditUserModal: React.FC<AdminEditUserModalProps> = ({
  isOpen,
  onClose,
  user,
  onSave,
  onUpdateMembership,
  loading,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [globalRole, setGlobalRole] = useState<GlobalRole>('USER');
  const [error, setError] = useState<string | null>(null);

  // Track tenant role changes: { tenantId: newRole }
  const [tenantRoleChanges, setTenantRoleChanges] = useState<Record<string, TenantRole>>({});

  const bgColor = useColorModeValue('white', 'gray.800');
  const selectBg = useColorModeValue('white', 'gray.700');
  const selectColor = useColorModeValue('gray.800', 'white');
  const optionBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Common styles for Select components
  const selectStyles = {
    bg: selectBg,
    color: selectColor,
    sx: {
      '& option': {
        bg: optionBg,
        color: selectColor,
      },
    },
  };

  // Check if user has tenant memberships
  const hasTenantMemberships = useMemo(() => {
    return user?.memberships && user.memberships.length > 0;
  }, [user]);

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setGlobalRole(user.globalRole);
      setTenantRoleChanges({});
      setError(null);
    }
  }, [user]);

  /**
   * Handle tenant role change
   */
  const handleTenantRoleChange = (tenantId: string, newRole: TenantRole) => {
    const originalRole = user?.memberships?.find(m => m.tenantId === tenantId)?.tenantRole;

    if (newRole === originalRole) {
      // Remove from changes if reverted to original
      setTenantRoleChanges(prev => {
        const updated = { ...prev };
        delete updated[tenantId];
        return updated;
      });
    } else {
      setTenantRoleChanges(prev => ({ ...prev, [tenantId]: newRole }));
    }
  };

  /**
   * Get current role for a tenant (changed or original)
   */
  const getTenantRole = (tenantId: string): TenantRole => {
    if (tenantRoleChanges[tenantId]) {
      return tenantRoleChanges[tenantId];
    }
    return user?.memberships?.find(m => m.tenantId === tenantId)?.tenantRole || 'USER';
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Build user profile changes
    const userChanges: UpdateAdminUserInput = {};

    if (displayName !== (user?.displayName || '')) {
      userChanges.displayName = displayName;
    }
    if (firstName !== (user?.firstName || '')) {
      userChanges.firstName = firstName;
    }
    if (lastName !== (user?.lastName || '')) {
      userChanges.lastName = lastName;
    }
    if (globalRole !== user?.globalRole) {
      userChanges.globalRole = globalRole;
    }

    // Save user profile changes if any
    const hasUserChanges = Object.keys(userChanges).length > 0;
    const hasMembershipChanges = Object.keys(tenantRoleChanges).length > 0;

    if (!hasUserChanges && !hasMembershipChanges) {
      onClose();
      return;
    }

    try {
      // Save user profile changes
      if (hasUserChanges) {
        const success = await onSave(userChanges);
        if (!success) {
          setError('Failed to update user profile. Please try again.');
          return;
        }
      }

      // Save membership changes
      if (hasMembershipChanges && onUpdateMembership && user) {
        for (const [tenantId, tenantRole] of Object.entries(tenantRoleChanges)) {
          const result = await onUpdateMembership(user.id, tenantId, { tenantRole });
          if (!result) {
            setError('Failed to update tenant role. Please try again.');
            return;
          }
        }
      }

      onClose();
    } catch {
      setError('An error occurred. Please try again.');
    }
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    setError(null);
    setTenantRoleChanges({});
    onClose();
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>Edit User</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              <Text fontSize="sm" color="gray.500">
                Editing: {user.email}
              </Text>

              <FormControl>
                <FormLabel>Display Name</FormLabel>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Display Name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>First Name</FormLabel>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Last Name</FormLabel>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last Name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Global Role</FormLabel>
                <Select
                  value={globalRole}
                  onChange={(e) => setGlobalRole(e.target.value as GlobalRole)}
                  {...selectStyles}
                >
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                </Select>
              </FormControl>

              {/* Tenant Memberships Section */}
              {hasTenantMemberships && onUpdateMembership && (
                <>
                  <Divider my={2} />
                  <Text fontWeight="semibold" fontSize="md">
                    Tenant Memberships
                  </Text>
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    Manage user roles within each tenant
                  </Text>

                  <VStack spacing={3} align="stretch">
                    {user.memberships?.map((membership) => (
                      <Box
                        key={membership.tenantId}
                        p={3}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                      >
                        <HStack justify="space-between" align="center">
                          <VStack align="start" spacing={0}>
                            <HStack>
                              <Text fontWeight="medium">{membership.tenantName}</Text>
                              {membership.isOwner && (
                                <Badge colorScheme="purple" size="sm">Owner</Badge>
                              )}
                            </HStack>
                            <Text fontSize="xs" color="gray.500">
                              {membership.tenantSlug}
                            </Text>
                          </VStack>
                          <Select
                            value={getTenantRole(membership.tenantId)}
                            onChange={(e) => handleTenantRoleChange(membership.tenantId, e.target.value as TenantRole)}
                            size="sm"
                            width="160px"
                            {...selectStyles}
                          >
                            <option value="USER">User</option>
                            <option value="TENANT_ADMIN">Tenant Admin</option>
                          </Select>
                        </HStack>
                        {tenantRoleChanges[membership.tenantId] && (
                          <Text fontSize="xs" color="orange.500" mt={1}>
                            Role will be changed to {tenantRoleChanges[membership.tenantId] === 'TENANT_ADMIN' ? 'Tenant Admin' : 'User'}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </VStack>
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" type="submit" isLoading={loading}>
              Save Changes
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default AdminEditUserModal;
